import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { format } from 'date-fns';


// Leaflet icon setup wrapper
let leafletInitialized = false;
function initLeaflet(L: any) {
  if (leafletInitialized) return;

  delete (L.Icon.Default.prototype as any)._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
  leafletInitialized = true;
}
// Run on module load to prevent first-render broken images
initLeaflet(L);

const SHADOW_URL = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png';
let defaultIcon: L.Icon.Default | null = null;
const colorIconCache: Record<string, L.Icon> = {};
export function buildColoredIcon(color: string): L.Icon {
  if (colorIconCache[color]) return colorIconCache[color];
  const icon = new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${color}.png`,
    iconRetinaUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
    shadowUrl: SHADOW_URL,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
  colorIconCache[color] = icon;
  return icon;
}
let HighlightedIcon: L.Icon | null = null;

export const getHighlightedIcon = () => {
  if (!HighlightedIcon) {
    HighlightedIcon = new L.Icon({
      iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png`,
      iconRetinaUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png`,
      shadowUrl: SHADOW_URL,
      iconSize: [35, 51],
      iconAnchor: [17, 51],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
      className: 'animate-bounce'
    });
  }
  return HighlightedIcon;
};

export const ArrowIcon = (angle: number) => new L.DivIcon({
  html: `<div style="transform: rotate(${angle}deg); font-size: 16px; color: #2563eb; font-weight: bold; text-shadow: 1px 1px 2px white; margin-top: -8px; margin-left: -6px;">➤</div>`,
  className: '',
  iconSize: [12, 12]
});

function deg2rad(deg: number) { return deg * (Math.PI / 180); }
export function calculateBearing(lat1: number, lon1: number, lat2: number, lon2: number) {
  const dLon = deg2rad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(deg2rad(lat2));
  const x = Math.cos(deg2rad(lat1)) * Math.sin(deg2rad(lat2)) - Math.sin(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.cos(dLon);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

export function MapFlyTo({ position }: { position: L.LatLngTuple | null }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 15, { animate: true, duration: 1.5 });
  }, [position, map]);
  return null;
}

export default function MapRenderer({
  filteredResponses, forms, formColorMap, highlightedResponseId, setHighlightedResponseId,
  flyToPos, mapLayer, pinLegend1, pinLegend2, getLegendDisplay, setSelectedResponseId, showTimeline
}: any) {
  return (
    <MapContainer
      center={[-15.7881864, -47.8901477]}
      zoom={10}
      className="w-full h-full z-0"
      zoomControl={false}
    >
      <MapFlyTo position={flyToPos} />
      <TileLayer attribution='&copy; Map Data' url={mapLayer.url} />
      {filteredResponses.map((r: any) => {
        const pos = r.deviceInfo?.geolocation;
        if (!pos) return null;
        const form = forms.find((f: any) => f.id === r.formId);
        const color = formColorMap[r.formId];

        if (!defaultIcon) defaultIcon = new L.Icon.Default();
        const icon = highlightedResponseId === r.id
          ? getHighlightedIcon()
          : (color ? buildColoredIcon(color) : defaultIcon);

        const leg1 = getLegendDisplay(pinLegend1, r);
        const leg2 = getLegendDisplay(pinLegend2, r);
        return (
          <Marker
            key={r.id}
            position={[pos.lat, pos.lng]}
            icon={icon}
            zIndexOffset={highlightedResponseId === r.id ? 1000 : 0}
            eventHandlers={{ click: () => setHighlightedResponseId(r.id) }}
          >
            {(leg1 || leg2) && (
              <Tooltip direction="bottom" offset={[0, 10]} opacity={0.95} permanent
                className="!bg-white !text-gray-800 !border-gray-300 !shadow-md !p-1.5 !rounded !text-[10px] !font-bold !font-sans">
                {leg1 && <div className="whitespace-nowrap">{leg1}</div>}
                {leg2 && <div className="whitespace-nowrap text-gray-500">{leg2}</div>}
              </Tooltip>
            )}
            <Popup className="font-sans text-sm">
              <div className="font-bold text-gray-800 mb-1">{form?.title}</div>
              <div className="text-xs text-gray-500 mb-3">
                {r.respondentName} · {!isNaN(new Date(r.startedAt).getTime()) ? format(new Date(r.startedAt), 'dd/MM HH:mm') : 'Data Inválida'}
              </div>
              <button
                onClick={() => setSelectedResponseId(r.id)}
                className="text-xs bg-blue-600 hover:bg-blue-700 transition-colors text-white px-3 py-2 rounded w-full font-medium shadow-sm"
              >
                Ver detalhes da resposta
              </button>
            </Popup>
          </Marker>
        );
      })}

      {showTimeline && filteredResponses.length > 1 && (() => {
        const positions = filteredResponses
          .map((r: any) => r.deviceInfo?.geolocation)
          .filter(Boolean)
          .map((g: any) => [g.lat, g.lng]);

        if (positions.length < 2) return null;

        const arrows = [];
        for (let i = 0; i < positions.length - 1; i++) {
          const p1 = positions[i];
          const p2 = positions[i + 1];
          // Skip if same point
          if (p1[0] === p2[0] && p1[1] === p2[1]) continue;

          const bearing = calculateBearing(p1[0], p1[1], p2[0], p2[1]);
          const midLat = (p1[0] + p2[0]) / 2;
          const midLng = (p1[1] + p2[1]) / 2;

          arrows.push(
            <Marker key={`arrow-${i}`} position={[midLat, midLng]} icon={ArrowIcon(bearing - 90)} interactive={false} />
          );
        }

        return (
          <>
            <Polyline
              positions={positions as any}
              pathOptions={{ color: '#2563eb', weight: 4, dashArray: '8, 8', opacity: 0.8 }}
            />
            {arrows}
          </>
        );
      })()}

    </MapContainer>
  );
}
